import { Percent } from 'lucide-react'
import { useEffect, useId } from 'react'
import { DistributionEditor } from '@/components/recurring/DistributionEditor'
import { DistributionResult } from '@/components/recurring/DistributionResult'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Select } from '@/components/ui/Select'
import { formatEuros } from '@/lib/format'
import { liveDistribution } from '@/lib/recurring-distribution'
import { draftLines, emptyShare, groupSubtotal } from '@/lib/recurring-draft'
import type { GroupDraft, ShareDraft } from '@/lib/recurring-draft'
import type { Beneficiary } from '@/lib/recurring-types'
import { toCents } from '@/lib/shares'

/**
 * De verdeling in zijn geheel: eerst de standaard per groep, dan de regels die
 * daarvan afwijken, dan wat er onderaan uitrolt. Geen uitklapper meer op de
 * groepskop en geen popover per regel, zodat de inhoud niet verspringt.
 */
export function DistributionTab({
  groups,
  onChange,
  beneficiaries,
  focusKey,
  onFocusHandled,
  readonly,
}: {
  groups: GroupDraft[]
  onChange: (next: GroupDraft[]) => void
  beneficiaries: Beneficiary[]
  focusKey: string | null
  onFocusHandled: () => void
  readonly: boolean
}) {
  const pickerLabelId = useId()
  const filled = groups.filter((group) => group.heading.trim() !== '')
  const lines = draftLines(groups)
  const overrides = lines.filter(({ line }) => line.ownDistribution !== null)
  const candidates = lines.filter(({ line }) => line.ownDistribution === null)

  // Een regel die de groep volgt heeft hier geen eigen blok; dan is de
  // groepsstandaard de plek waar het antwoord staat.
  // biome-ignore lint/correctness/useExhaustiveDependencies: alleen een nieuwe focusKey mag scrollen; lines en onFocusHandled wisselen elke render en zouden het effect laten rondlopen
  useEffect(() => {
    if (!focusKey) return
    const owner = lines.find(({ line }) => line.key === focusKey)
    const target =
      document.getElementById(`share-${focusKey}`) ??
      (owner ? document.getElementById(`share-group-${owner.group.key}`) : null)
    target?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    onFocusHandled()
  }, [focusKey])

  const patchGroup = (target: GroupDraft, patch: Partial<GroupDraft>) =>
    onChange(groups.map((group) => (group.key === target.key ? { ...group, ...patch } : group)))

  const patchLine = (lineKey: string, patch: Partial<GroupDraft['lines'][number]>) =>
    onChange(
      groups.map((group) => ({
        ...group,
        lines: group.lines.map((line) => (line.key === lineKey ? { ...line, ...patch } : line)),
      })),
    )

  const startOverride = (lineKey: string) => {
    const found = lines.find(({ line }) => line.key === lineKey)
    if (!found) return
    const seed: ShareDraft[] =
      found.group.distribution.length > 0
        ? found.group.distribution.map((share) => ({ ...share, key: `${share.key}-own` }))
        : [emptyShare(`s${lineKey}`)]
    patchLine(lineKey, { ownDistribution: seed })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Standaard per groep" subtitle="Geldt voor elke regel in de groep die niet zelf afwijkt" />

        {filled.length === 0 ? (
          <p className="mt-4 text-sm text-ink-faint">Voeg eerst een groep met een kop toe op het tabblad Inhoud.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {filled.map((group) => (
              <div key={group.key} id={`share-group-${group.key}`} className="rounded-lg border border-hairline p-3">
                <div className="mb-2.5 flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-semibold text-ink">{group.heading}</span>
                  <span className="money shrink-0 text-sm text-ink-soft">{formatEuros(groupSubtotal(group) / 100)}</span>
                </div>

                {group.distribution.length > 0 ? (
                  <>
                    <DistributionEditor
                      shares={group.distribution}
                      onChange={(next) => patchGroup(group, { distribution: next })}
                      beneficiaries={beneficiaries}
                      amountCents={groupSubtotal(group)}
                      amountLabel="de groep"
                      readonly={readonly}
                    />
                    {readonly ? null : (
                      <button
                        type="button"
                        className="btn-ghost mt-2 h-7 px-1.5 text-xs text-ink-soft"
                        onClick={() => patchGroup(group, { distribution: [] })}
                      >
                        Verdeling weghalen
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-ink-faint">Volgt het verdeel-script van de klant.</span>
                    {readonly ? null : (
                      <button
                        type="button"
                        className="btn-secondary h-8 px-2.5 text-sm"
                        onClick={() => patchGroup(group, { distribution: [emptyShare(`s${group.key}`)] })}
                      >
                        Verdeling vastleggen
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Regels met een eigen verdeling"
          subtitle="Deze regels wijken af van de standaard van hun groep"
          action={
            overrides.length > 0 ? (
              <span className="num text-sm text-ink-faint">
                {overrides.length} {overrides.length === 1 ? 'regel' : 'regels'}
              </span>
            ) : null
          }
        />

        {overrides.length === 0 ? (
          <EmptyState
            className="mt-4"
            icon={Percent}
            title="Geen regels met een eigen verdeling"
            description="Elke regel volgt de standaard van zijn groep."
            action={readonly ? null : <Picker labelId={pickerLabelId} candidates={candidates} onPick={startOverride} />}
          />
        ) : (
          <div className="mt-4 space-y-3">
            {overrides.map(({ group, line }) => (
              <div key={line.key} id={`share-${line.key}`} className="rounded-lg border border-hairline p-3">
                <div className="mb-2.5 flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-ink">
                    <span className="text-ink-faint">{group.heading} · </span>
                    <span className="font-semibold">{line.description}</span>
                  </span>
                  <span className="money shrink-0 text-sm text-ink-soft">{formatEuros(toCents(line.amount) / 100)}</span>
                </div>

                <DistributionEditor
                  shares={line.ownDistribution ?? []}
                  onChange={(next) => patchLine(line.key, { ownDistribution: next })}
                  beneficiaries={beneficiaries}
                  amountCents={toCents(line.amount)}
                  amountLabel="de regel"
                  readonly={readonly}
                />

                {readonly ? null : (
                  <button
                    type="button"
                    className="btn-ghost mt-2 h-7 px-1.5 text-xs text-ink-soft"
                    onClick={() => patchLine(line.key, { ownDistribution: null })}
                  >
                    Terug naar de groep
                  </button>
                )}
              </div>
            ))}

            {readonly ? null : (
              <div className="pt-1">
                <Picker labelId={pickerLabelId} candidates={candidates} onPick={startOverride} />
              </div>
            )}
          </div>
        )}
      </Card>

      <DistributionResult result={liveDistribution(groups, beneficiaries)} />
    </div>
  )
}

function Picker({
  labelId,
  candidates,
  onPick,
}: {
  labelId: string
  candidates: ReturnType<typeof draftLines>
  onPick: (lineKey: string) => void
}) {
  if (candidates.length === 0) {
    return <p className="text-sm text-ink-faint">Alle regels hebben al een eigen verdeling.</p>
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span id={labelId} className="text-sm text-ink-soft">Laat een regel afwijken</span>
      <Select
        value=""
        onValueChange={onPick}
        options={candidates.map(({ group, line }) => ({ value: line.key, label: `${group.heading} · ${line.description}` }))}
        placeholder="Kies een regel"
        ariaLabelledby={labelId}
        className="w-[min(100%,26rem)]"
      />
    </div>
  )
}
